import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useRenderModeCtx } from '@/Pages/Graph/src/control/state';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { useFeedbackConfigs } from '../../../hooks/useSwr';
import { ExampleSchemaWithRuns, SessionSchema } from '../../../types/schema';
import {
  ATTACHMENTS_HIDDEN_LOCAL_STORAGE_KEY,
  DEFAULT_SESSION_COMPARE_SETTINGS_VALUES,
  DatasetSessionCompareBarChartSpec,
  DatasetSessionCompareDefaults,
  DatasetSessionCompareDefaultsProps,
  DatasetSessionCompareSettings,
  FEEDBACK_HIDDEN_LOCAL_STORAGE_KEY,
  HIDDEN_FEEDBACK_COLUMNS_LOCAL_STORAGE_KEY,
  HIDDEN_METRICS_COLUMNS_LOCAL_STORAGE_KEY,
  LANGUAGE_LOCAL_STORAGE_KEY,
  METRICS_HIDDEN_LOCAL_STORAGE_KEY,
  REFERENCE_INPUT_HIDDEN_LOCAL_STORAGE_KEY,
  REFERENCE_OUTPUT_HIDDEN_LOCAL_STORAGE_KEY,
  SELECTED_CHARTS_LOCAL_STORAGE_KEY,
  TEXT_DISPLAY_MODE_LOCAL_STORAGE_KEY,
  TEXT_DISPLAY_MODE_SEARCH_PARAM,
  TextDisplayMode,
} from '../constants';

export const useComparisonViewDisplaySettings = ({
  aggregateFeedbackKeys,
  aggregateSessionLevelFeedbackKeys,
  localStorageStatePrefix = '',
  defaultSettings,
  searchParams,
  setSearchParams,
  examples,
  isSingleSession,
}: {
  aggregateFeedbackKeys?: string[];
  aggregateSessionLevelFeedbackKeys?: string[];
  localStorageStatePrefix?: string;
  defaultSettings?: DatasetSessionCompareDefaultsProps;
  searchParams?: ReturnType<typeof useSearchParams>[0];
  setSearchParams?: ReturnType<typeof useSearchParams>[1];
  examples?: ExampleSchemaWithRuns[];
  isSingleSession?: boolean;
}): DatasetSessionCompareSettings => {
  const feedbackKeysToFetch = useMemo(
    () =>
      (aggregateFeedbackKeys ?? []).concat(
        aggregateSessionLevelFeedbackKeys ?? []
      ),
    [aggregateFeedbackKeys, aggregateSessionLevelFeedbackKeys]
  );

  const feedbackConfigs = useFeedbackConfigs(
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      skip: !feedbackKeysToFetch || feedbackKeysToFetch.length === 0,
    },
    {
      key: feedbackKeysToFetch,
    }
  );

  const barChartSpecs: DatasetSessionCompareBarChartSpec[] | null = useMemo(
    () =>
      !aggregateSessionLevelFeedbackKeys || !aggregateFeedbackKeys
        ? null
        : [
            ...aggregateSessionLevelFeedbackKeys.map((key) => {
              let minVal: number | undefined = undefined;
              let maxVal: number | undefined = undefined;
              const feedbackConfig = feedbackConfigs.data?.find(
                (c) => c.feedback_key === key
              )?.feedback_config;
              if (feedbackConfig?.type === 'continuous') {
                minVal = feedbackConfig.min ?? undefined;
                maxVal = feedbackConfig.max ?? undefined;
              }
              return {
                accessor: (row: SessionSchema) =>
                  row.session_feedback_stats?.[key]?.avg,
                name: key,
                isFeedback: false,
                isSessionLevelFeedback: true,
                feedbackKey: key,
                isErrorRate: false,
                isLatency: false,
                minimumValue: minVal,
                maximumValue: maxVal,
                yAxisLabel: 'Avg Score',
              };
            }),
            ...aggregateFeedbackKeys.map((key) => {
              let minVal: number | undefined = undefined;
              let maxVal: number | undefined = undefined;
              const feedbackConfig = feedbackConfigs.data?.find(
                (c) => c.feedback_key === key
              )?.feedback_config;
              if (feedbackConfig?.type === 'continuous') {
                minVal = feedbackConfig.min ?? undefined;
                maxVal = feedbackConfig.max ?? undefined;
              }
              return {
                accessor: (row: SessionSchema) =>
                  row.feedback_stats?.[key]?.avg,
                name: key,
                isFeedback: true,
                isSessionLevelFeedback: false,
                feedbackKey: key,
                isErrorRate: false,
                isLatency: false,
                minimumValue: minVal,
                maximumValue: maxVal,
                yAxisLabel: 'Avg Score',
              };
            }),
            {
              accessor: (row: SessionSchema) => row.latency_p50,
              name: 'P50 Latency',
              isLatency: true,
              latencyType: 'P50',
              isFeedback: false,
              isSessionLevelFeedback: false,
              isErrorRate: false,
              minimumValue: 0,
              yAxisLabel: 'P50 Latency (s)',
            },
            {
              accessor: (row: SessionSchema) => row.latency_p99,
              name: 'P99 Latency',
              isLatency: true,
              latencyType: 'P99',
              isFeedback: false,
              isSessionLevelFeedback: false,
              isErrorRate: false,
              minimumValue: 0,
              yAxisLabel: 'P99 Latency (s)',
            },
            {
              accessor: (row: SessionSchema) => row.total_tokens,
              name: 'Total Tokens',
              isLatency: false,
              isFeedback: false,
              isSessionLevelFeedback: false,
              isErrorRate: false,
              minimumValue: 0,
              yAxisLabel: 'Total Tokens',
            },
            {
              accessor: (row: SessionSchema) => row.prompt_tokens,
              name: 'Prompt Tokens',
              isLatency: false,
              isFeedback: false,
              isSessionLevelFeedback: false,
              isErrorRate: false,
              minimumValue: 0,
              yAxisLabel: 'Prompt Tokens',
            },
            {
              accessor: (row: SessionSchema) => row.completion_tokens,
              name: 'Completion Tokens',
              isLatency: false,
              isFeedback: false,
              isSessionLevelFeedback: false,
              isErrorRate: false,
              minimumValue: 0,
              yAxisLabel: 'Completion Tokens',
            },
          ],
    [
      aggregateFeedbackKeys,
      aggregateSessionLevelFeedbackKeys,
      feedbackConfigs.data,
    ]
  );

  const settings: DatasetSessionCompareDefaults = useMemo(() => {
    const base = {
      ...DEFAULT_SESSION_COMPARE_SETTINGS_VALUES,
      ...(defaultSettings ?? {}),
    };
    return base;
  }, [defaultSettings]);

  const foreignDataRenderMode = useRenderModeCtx({
    renderMode: 'pretty',
  });

  const [language, setLanguage] = useLocalStorageState(
    `ls:${localStorageStatePrefix}${LANGUAGE_LOCAL_STORAGE_KEY}`,
    settings.language
  );

  const [_textDisplayMode, _setTextDisplayMode] = useLocalStorageState(
    `ls:${localStorageStatePrefix}${TEXT_DISPLAY_MODE_LOCAL_STORAGE_KEY}`,
    settings.textDisplayMode
  );

  const textDisplayModeParam = searchParams?.get(
    TEXT_DISPLAY_MODE_SEARCH_PARAM
  );
  const textDisplayMode =
    (textDisplayModeParam as TextDisplayMode) || _textDisplayMode;

  const setTextDisplayMode = useCallback(
    (value: TextDisplayMode) => {
      _setTextDisplayMode(value);
      setSearchParams?.((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set(TEXT_DISPLAY_MODE_SEARCH_PARAM, value);
        return newParams;
      });
    },
    [setSearchParams, _setTextDisplayMode]
  );

  const isShowingDiff = textDisplayMode === TextDisplayMode.DIFF;
  const setIsShowingDiff = useCallback(
    (diffMode: boolean) => {
      setTextDisplayMode(
        diffMode ? TextDisplayMode.DIFF : TextDisplayMode.FULL
      );
      if (diffMode) {
        setLanguage('json');
      }
    },
    [setTextDisplayMode, setLanguage]
  );

  useEffect(() => {
    if (textDisplayMode === TextDisplayMode.DIFF && language === 'pretty') {
      setLanguage('json');
    }
  }, [textDisplayMode, setLanguage, language]);

  const [hideMetrics, setHideMetrics] = useLocalStorageState(
    `ls:${localStorageStatePrefix}${METRICS_HIDDEN_LOCAL_STORAGE_KEY}`,
    settings.hideMetrics
  );
  const [hideFeedback, setHideFeedback] = useLocalStorageState(
    `ls:${localStorageStatePrefix}${FEEDBACK_HIDDEN_LOCAL_STORAGE_KEY}`,
    settings.hideFeedback
  );
  const [isReferenceOutputHidden, _setIsReferenceOutputHidden] = useState(
    settings.isReferenceOutputHidden
  );
  const [, _setIsReferenceOutputHiddenLocalStorage] = useLocalStorageState(
    `ls:${localStorageStatePrefix}${REFERENCE_OUTPUT_HIDDEN_LOCAL_STORAGE_KEY}`,
    settings.isReferenceOutputHidden
  );

  const setIsReferenceOutputHidden = useCallback(
    (value: boolean | ((prevState: boolean) => boolean)) => {
      _setIsReferenceOutputHidden(value);
      _setIsReferenceOutputHiddenLocalStorage(value);
    },
    [_setIsReferenceOutputHidden, _setIsReferenceOutputHiddenLocalStorage]
  );

  const [isReferenceInputHidden, setIsReferenceInputHidden] =
    useLocalStorageState(
      `ls:${localStorageStatePrefix}${REFERENCE_INPUT_HIDDEN_LOCAL_STORAGE_KEY}`,
      settings.isReferenceInputHidden
    );
  const [isAttachmentsHidden, setIsAttachmentsHidden] = useLocalStorageState(
    `ls:${localStorageStatePrefix}${ATTACHMENTS_HIDDEN_LOCAL_STORAGE_KEY}`,
    settings.isAttachmentsHidden
  );

  const [selectedChartsState, setSelectedChartsState] = useLocalStorageState<
    | {
        name: string;
        isFeedback: boolean;
        isSessionLevelFeedback: boolean;
      }[]
    | null
  >(`ls:${localStorageStatePrefix}${SELECTED_CHARTS_LOCAL_STORAGE_KEY}`, null);

  useEffect(() => {
    const allExamplesDontHaveReferenceOutput =
      examples &&
      examples.every((example) => {
        if (example.outputs == null) {
          return true;
        }

        if (typeof example.outputs !== 'object') {
          // For type safety, but should never happen
          return false;
        }

        if (Array.isArray(example.outputs)) {
          return example.outputs.length === 0;
        }

        return Object.keys(example.outputs).length === 0;
      });
    if (allExamplesDontHaveReferenceOutput) {
      _setIsReferenceOutputHidden(true);
    }
  }, [_setIsReferenceOutputHidden, examples]);

  // Set selectedChartsState when barChartSpecs is populated if localstorage is empty
  // Prioritize feedback score charts if they exist
  useEffect(() => {
    if (selectedChartsState === null && barChartSpecs) {
      // Filter to only include feedback charts (both session-level and run-level)
      const feedbackCharts = barChartSpecs.filter(
        (chart) => chart.isFeedback || chart.isSessionLevelFeedback
      );

      // If we have feedback charts, only show those by default
      if (feedbackCharts.length > 0) {
        setSelectedChartsState(feedbackCharts);
      } else {
        // Fall back to all charts if no feedback charts are available
        setSelectedChartsState(barChartSpecs);
      }
    }
  }, [barChartSpecs, selectedChartsState, setSelectedChartsState]);

  const selectedCharts = useMemo(
    () =>
      (selectedChartsState ?? [])
        ?.map((sel) =>
          barChartSpecs?.find(
            (spec) =>
              spec.name === sel.name &&
              spec.isFeedback === sel.isFeedback &&
              spec.isSessionLevelFeedback === sel.isSessionLevelFeedback
          )
        )
        .filter(Boolean) as DatasetSessionCompareBarChartSpec[],
    [barChartSpecs, selectedChartsState]
  );

  const [hiddenFeedbackColumns, setHiddenFeedbackColumns] =
    useLocalStorageState<string[]>(
      `ls:${localStorageStatePrefix}${HIDDEN_FEEDBACK_COLUMNS_LOCAL_STORAGE_KEY}`,
      settings.hiddenFeedbackColumns
    );

  const [hiddenMetricsColumns, setHiddenMetricsColumns] = useLocalStorageState<
    string[]
  >(
    `ls:${localStorageStatePrefix}${HIDDEN_METRICS_COLUMNS_LOCAL_STORAGE_KEY}`,
    settings.hiddenMetricsColumns
  );

  const setSelectedCharts = useCallback(
    (setter: React.SetStateAction<DatasetSessionCompareBarChartSpec[]>) => {
      setSelectedChartsState((prev) => {
        const newSelectedCharts: {
          name: string;
          isFeedback: boolean;
          isSessionLevelFeedback: boolean;
        }[] = [];
        const selectedChartsValue =
          setter instanceof Function ? setter(selectedCharts) : setter;
        for (const chart of selectedChartsValue) {
          const chartSpec = barChartSpecs?.find(
            (value) =>
              value.name === chart.name &&
              value.isFeedback === chart.isFeedback &&
              value.isSessionLevelFeedback === chart.isSessionLevelFeedback
          );
          if (chartSpec) {
            newSelectedCharts.push({
              name: chartSpec.name,
              isFeedback: chartSpec.isFeedback,
              isSessionLevelFeedback: chartSpec.isSessionLevelFeedback,
            });
          }
        }
        for (const chart of prev ?? []) {
          const chartSpec = barChartSpecs?.find(
            // Add any charts that don't exist in the current comparison to the list as well, to preserve for other pages
            (value) =>
              value.name === chart.name &&
              value.isFeedback === chart.isFeedback &&
              value.isSessionLevelFeedback === chart.isSessionLevelFeedback
          );
          if (barChartSpecs && !chartSpec) {
            newSelectedCharts.push(chart);
          }
        }
        return newSelectedCharts;
      });
    },
    [barChartSpecs, selectedCharts, setSelectedChartsState]
  );

  const canResetDefaults = useMemo(
    () =>
      selectedCharts.length > 0 ||
      hideFeedback !== settings.hideFeedback ||
      hideMetrics !== settings.hideMetrics ||
      isReferenceInputHidden !== settings.isReferenceInputHidden ||
      isReferenceOutputHidden !== settings.isReferenceOutputHidden ||
      isAttachmentsHidden !== settings.isAttachmentsHidden ||
      hiddenMetricsColumns.length > 0 ||
      hiddenFeedbackColumns.length > 0,
    [
      selectedCharts.length,
      settings.hideFeedback,
      settings.hideMetrics,
      settings.isReferenceInputHidden,
      settings.isReferenceOutputHidden,
      settings.isAttachmentsHidden,
      hideFeedback,
      hideMetrics,
      isReferenceInputHidden,
      isReferenceOutputHidden,
      isAttachmentsHidden,
      hiddenMetricsColumns.length,
      hiddenFeedbackColumns.length,
    ]
  );

  const onResetDefaults = useCallback(() => {
    setSelectedCharts([]);
    setHideFeedback(settings.hideFeedback);
    setHideMetrics(settings.hideMetrics);
    setIsReferenceInputHidden(settings.isReferenceInputHidden);
    setIsAttachmentsHidden(settings.isAttachmentsHidden);
    setIsReferenceOutputHidden(settings.isReferenceOutputHidden);
    setHiddenMetricsColumns([]);
    setHiddenFeedbackColumns([]);
  }, [
    settings,
    setHideFeedback,
    setHideMetrics,
    setIsReferenceInputHidden,
    setIsReferenceOutputHidden,
    setIsAttachmentsHidden,
    setSelectedCharts,
    setHiddenMetricsColumns,
    setHiddenFeedbackColumns,
  ]);

  return {
    textDisplayMode,
    setTextDisplayMode,
    hideMetrics: isSingleSession ? false : hideMetrics,
    setHideMetrics,
    hideFeedback: isSingleSession ? false : hideFeedback,
    setHideFeedback,
    isReferenceInputHidden: isSingleSession ? false : isReferenceInputHidden,
    setIsReferenceInputHidden,
    isAttachmentsHidden: isSingleSession ? false : isAttachmentsHidden,
    setIsAttachmentsHidden,
    isReferenceOutputHidden: isSingleSession ? false : isReferenceOutputHidden,
    setIsReferenceOutputHidden,
    language,
    setLanguage,
    selectedCharts,
    setSelectedCharts,
    barChartSpecs,
    onResetDefaults,
    canResetDefaults,
    hiddenFeedbackColumns: isSingleSession ? [] : hiddenFeedbackColumns,
    setHiddenFeedbackColumns,
    hiddenMetricsColumns: isSingleSession ? [] : hiddenMetricsColumns,
    setHiddenMetricsColumns,
    isShowingDiff,
    setIsShowingDiff,
    foreignDataRenderMode,
  };
};
